import type { BBox, Point } from 'geojson'
import {
	type EasingOptions,
	LngLat,
	type LngLatBoundsLike,
	type LngLatLike,
	type MapEvent,
	type MapEvents,
	type MapEventType,
} from 'mapbox-gl'
import { useEffect } from 'react'
import { router, urlHash } from '../../router.tsx'
import { useMap, useMapState } from '../../states.ts'
import { layerOrder } from '../layers/layers.tsx'

export const flyTo = (map: mapboxgl.Map, center: LngLatLike | Point, options?: EasingOptions) => {
	const centerLngLat = 'coordinates' in center ? (center.coordinates as [number, number]) : center
	const { lat, lng } = LngLat.convert(centerLngLat)
	updateHash(map, { lat, lon: lng })
	map.flyTo({ center: centerLngLat, zoom: getDefaultZoom(map), curve: 0.8, ...sanitizeOptions(options) })
}

export const fitBounds = (map: mapboxgl.Map, bounds: LngLatBoundsLike | BBox, _options?: EasingOptions) => {
	const options = sanitizeOptions(_options)
	const cameraPlacement = map.cameraForBounds(safeBounds(bounds), options)
	if (!cameraPlacement?.center) return
	const { lat, lng } = LngLat.convert(cameraPlacement.center)
	updateHash(map, { lat, lon: lng, z: cameraPlacement.zoom })
	map.fitBounds(safeBounds(bounds), { curve: 0.8, ...options })
}

export const updateHash = (map: mapboxgl.Map, coords: { lat: number; lon: number; z?: number }) => {
	useMapState.setState({ viewState: coords })
	router.navigate({ hash: urlHash.format({ z: getDefaultZoom(map), ...coords }), replace: true, search: true })?.catch(console.error)
}

const getDefaultZoom = (map: mapboxgl.Map) => Math.max(5, map.getZoom())
const safeBounds = (bounds: LngLatBoundsLike | BBox): LngLatBoundsLike => {
	if (Array.isArray(bounds) && bounds.length === 6) return [bounds[0], bounds[1], bounds[2], bounds[3]]
	return bounds
}

// `retainPadding` defaults to `true` in `fitBounds`, `flyTo` etc. `retainPadding=true` persists the padding as "global map padding ".
// Asymmetric global padding values for top/bottom or left/right breaks the Aerisweather MapsGL layer rendering,
// so let's make sure that `retainPadding` is false
// https://docs.mapbox.com/mapbox-gl-js/api/properties/#paddingoptions
const sanitizeOptions = (options?: EasingOptions): EasingOptions => ({ ...options, retainPadding: false })

export const dragPanOptions = { deceleration: Infinity }

/** Each map style needs to include all icons defined in
 * @link file://./../../data/locations.tsx locationIcons
 *  Icons are added via [Mapbox Studio](https://docs.mapbox.com/studio-manual/examples/custom-icon/).
 */
export const mapStyles = {
	light: 'mapbox://styles/vaisalaerg/cmejos4e0000k01qt7cox00ur',
	dark: 'mapbox://styles/vaisalaerg/cmejowvko00ow01s6aul16evc',
	satellite: 'mapbox://styles/vaisalaerg/cmejoxqi2000b01s82s1een81',
}

export const useMapCursorPoint = (onPointChange: (point: [number, number] | undefined) => void) => {
	const map = useMap((s) => s.map)
	useEffect(() => {
		if (!map) return
		const updatePoint = (e: MapEvent) => onPointChange(getPoint(e))
		map.on('drag', updatePoint)
		map.on('mouseout', updatePoint)
		map.on('mousemove', updatePoint)
		return () => {
			map.off('drag', updatePoint)
			map.off('mouseout', updatePoint)
			map.off('mousemove', updatePoint)
		}
	}, [map, onPointChange])
}

export const useMapCursorLngLat = (onLngLatChange: (lngLat: [number, number] | undefined) => void) => {
	const map = useMap((s) => s.map)
	useMapCursorPoint((newPoint) => map && onLngLatChange(newPoint ? map.unproject(newPoint).toArray() : undefined))
}

export const createZIndexLayers = (map: mapboxgl.Map) => {
	// The mapbox map instance is reused in different views. `onLoad` is called in every view, but the layers need to be created only once.
	const hasZIndexLayers = map.getLayer('z-index-layer-0') !== undefined
	if (!hasZIndexLayers) {
		for (let idx = layerOrder.length - 1; idx >= 0; idx--) {
			const id = layerOrder[idx]?.id
			if (!id) continue
			const layerId = `z-index-layer-${idx}`
			const beforeId = layerOrder[idx + 1]?.id ? `z-index-layer-${idx + 1}` : (layerOrder[idx + 1]?.mapId ?? undefined)
			map.addLayer({ id: layerId, type: 'slot' }, beforeId)
		}
	}
}

const getPoint = (e: MapEvent): [number, number] | undefined => {
	if (isType(e, 'mousemove')) return [e.point.x, e.point.y]
	if (isType(e, 'drag') && e.originalEvent instanceof MouseEvent && e.originalEvent.target === e.target.getCanvas()) {
		return [e.originalEvent.clientX, e.originalEvent.clientY]
	}
}
const isType = <T extends MapEventType>(e: unknown, type: T): e is MapEvents[T] => (e as MapEvent)?.type === type
